<script lang="ts">
  import { type PlayerInterface } from '@foundation-player/loader';
  import { getContext } from 'svelte';
  import Play from '../../shared/assets/icons/Play.svelte';
  import PlayPause from '../../shared/components/PlayPause.svelte';
  import ReplayButton from '../../shared/components/ReplayButton.svelte';
  import ShareButton from '../../shared/components/ShareButton.svelte';
  import Volume from '../../shared/components/Volume.svelte';
  import type { FocusLayer } from '../../shared/focus-manager/focus-types';
  import type { PlayerStateContext } from '../../shared/types/player-state';
  import type { MediaModeOptions } from '../types/internal/media-mode';
  import type { UIStateContext } from '../types/internal/ui-state';
  import AudioTrackSelector from './AudioTrackSelector.svelte';
  import Fullscreen from './Fullscreen.svelte';
  import PlaybackSpeed from './PlaybackSpeed.svelte';
  import SeekBar from './SeekBar.svelte';
  import SeekButton from './SeekButton.svelte';
  import StartButton from './StartButton.svelte';
  import TextTrackSelector from '../../shared/components/TextTrackSelector.svelte';

  /** The element which should be set to fullscreen */
  export let fullscreenElement: HTMLElement;
  export let player: PlayerInterface | undefined = undefined;
  export let focusLayer: FocusLayer;

  const mediaModeOptions = getContext<MediaModeOptions>('mediaModeOptions');

  const { waitingForFirstInteraction } = getContext<UIStateContext>('uiState');
  const { linearAdPlaying, liveMedium, sessionState } = getContext<PlayerStateContext>('playerState');

  $: isFullscreenAvailable = mediaModeOptions.hasFullscreen;
  $: isPlaybackSpeedAvailable = !$liveMedium && !$linearAdPlaying;
  $: isSeekingAvailable = mediaModeOptions.hasSeekButtonsInControls && !$liveMedium;
  $: showReplayButton = !$liveMedium && $sessionState === 'ended';
</script>

<div class="controls">
  <div class="top-control-bar">
    <SeekBar {player} {focusLayer} focusName="seekbarSlider" />
  </div>

  <div class="bottom-control-bar">
    <div class="control-container">
      {#if $waitingForFirstInteraction}
        <StartButton {focusLayer} focusName="start" />
      {:else if player}
        {#if showReplayButton}
          <ReplayButton {player} {focusLayer} focusName="replay">
            <Play aria-hidden={true} />
          </ReplayButton>
        {:else}
          <PlayPause {player} {focusLayer} focusName="playPause" />
        {/if}
      {/if}

      {#if player}
        {#if isSeekingAvailable}
          <SeekButton {player} seekOffsetInSeconds={-10} {focusLayer} focusName="seekBackward" />
          <SeekButton {player} seekOffsetInSeconds={10} {focusLayer} focusName="seekForward" />
        {/if}

        <Volume {player} {focusLayer} focusName="volume" />
      {/if}
    </div>

    <div class="control-container">
      {#if player}
        <ShareButton {focusLayer} focusName="share" />

        {#if !$linearAdPlaying}
          <TextTrackSelector {player} {focusLayer} focusName="textTrack" />
          <AudioTrackSelector {player} {focusLayer} focusName="audioTrack" />
        {/if}

        {#if isPlaybackSpeedAvailable}
          <PlaybackSpeed {player} {focusLayer} focusName="playbackSpeed" />
        {/if}

        {#if isFullscreenAvailable}
          <Fullscreen {fullscreenElement} {player} {focusLayer} focusName="fullscreen" />
        {/if}
      {/if}
    </div>
  </div>
</div>

<style module>
  .controls {
    display: flex;
    flex-direction: column;
    margin-top: auto;
    cursor: auto;
  }

  .bottom-control-bar {
    display: flex;
    flex-direction: row;
    flex: 1;
    justify-content: space-between;
    /* push buttons to the sides as otherwise they would look
    badly aligned from the rest of the controls */
    margin-left: -10px;
    margin-right: -10px;
  }

  .control-container {
    flex: 1;
    display: flex;
    justify-content: center;
    /* Min value (0px) ends at 400 px. Max value (20px) starts at 900 px. */
    /* https://modern-fluid-typography.vercel.app/ */
    gap: clamp(0px, 4cqw - 1em, 20px);
  }

  /* When creating the player on demand, there is a time between the user
   * clicking the start button and the player being ready where the controls are
   * empty - no element is rendered.
   * In audio mode, this would lead to a layout shift. We could either render
   * any button (possibly disabled) or set the minimum height of the container
   * to the height of a button.
   * This will break if the button size should increase, but for now, this seems
   * better than to render any button.
   */
  :global([data-mode='audio']) .control-container {
    min-height: 44px;
  }

  .control-container:first-child {
    justify-content: flex-start; /* fallback for old browsers */
    justify-content: start;
  }

  .control-container:last-child {
    justify-content: flex-end; /* fallback for old browsers */
    justify-content: end;
  }
</style>
